;(function () {
	const IMAGE_WIDTHS = [180, 360, 533, 720, 940, 1066, 1200, 1400, 1600]

	const PRODUCT_FIELDS = `
		fragment ProductCard on Product {
			id
			title
			handle
			vendor
			availableForSale
			featuredImage {
				url
				altText
				width
				height
			}
			priceRange {
				minVariantPrice {
					amount
					currencyCode
				}
			}
			compareAtPriceRange {
				minVariantPrice {
					amount
					currencyCode
				}
			}
		}
	`

	const COLLECTION_QUERY = `
		query ExternalCollection($handle: String!, $first: Int!, $after: String) {
			collection(handle: $handle) {
				handle
				products(first: $first, after: $after) {
					pageInfo {
						hasNextPage
						endCursor
					}
					nodes {
						...ProductCard
					}
				}
			}
		}
		${PRODUCT_FIELDS}
	`

	const PRODUCTS_QUERY = `
		query ExternalProducts($first: Int!, $after: String) {
			products(first: $first, after: $after) {
				pageInfo {
					hasNextPage
					endCursor
				}
				nodes {
					...ProductCard
				}
			}
		}
		${PRODUCT_FIELDS}
	`

	function escapeHtml(value) {
		return String(value == null ? '' : value)
			.replace(/&/g, '&amp;')
			.replace(/</g, '&lt;')
			.replace(/>/g, '&gt;')
			.replace(/"/g, '&quot;')
			.replace(/'/g, '&#39;')
	}

	function formatMoney(amount, currencyCode) {
		const value = Number(amount)
		if (!isFinite(value)) return ''

		try {
			return new Intl.NumberFormat(document.documentElement.lang || undefined, {
				style: 'currency',
				currency: currencyCode,
			}).format(value)
		} catch (error) {
			return `${value.toFixed(2)} ${currencyCode || ''}`.trim()
		}
	}

	function imageUrlAtWidth(url, width) {
		try {
			const parsed = new URL(url)
			parsed.searchParams.set('width', String(width))
			return parsed.href
		} catch (error) {
			return url
		}
	}

	function buildSrcset(image) {
		const maxWidth = Number(image.width) || IMAGE_WIDTHS[IMAGE_WIDTHS.length - 1]
		const widths = IMAGE_WIDTHS.filter((width) => width <= maxWidth)

		if (!widths.length) widths.push(maxWidth)

		return widths
			.map((width) => `${imageUrlAtWidth(image.url, width)} ${width}w`)
			.join(', ')
	}

	function buildProductUrl(settings, handle) {
		const path = String(settings.productPathPattern || '/products/{handle}')
			.replace('{handle}', encodeURIComponent(handle))

		try {
			const url = new URL(path, settings.externalBaseUrl)

			if (url.protocol !== 'https:' && url.protocol !== 'http:') return '#'

			if (settings.utmSource) {
				url.searchParams.set('utm_source', settings.utmSource)
				url.searchParams.set('utm_medium', 'referral')
			}
			return url.href
		} catch (error) {
			return '#'
		}
	}

	function renderPrice(product, settings) {
		const price = product.priceRange && product.priceRange.minVariantPrice
		if (!price) return ''

		const compareAt =
			product.compareAtPriceRange &&
			product.compareAtPriceRange.minVariantPrice

		const onSale =
			!!compareAt && Number(compareAt.amount) > Number(price.amount)

		const moneyPrice = formatMoney(price.amount, price.currencyCode)
		const moneyCompareAt = onSale
			? formatMoney(compareAt.amount, compareAt.currencyCode)
			: ''

		const classes = ['price', 'mini-price']
		if (!product.availableForSale) classes.push('price--sold-out')
		if (onSale) classes.push('price--on-sale')

		return `
			<div class="${classes.join(' ')}">
				<dl>
					<div class="price__regular">
						<dt>
							<span class="visually-hidden visually-hidden--inline">${escapeHtml(settings.textRegularPrice)}</span>
						</dt>
						<dd class="price__last">
							<span class="price-item price-item--regular">${escapeHtml(moneyPrice)}</span>
						</dd>
					</div>
					<div class="price__sale">
						<dt>
							<span class="visually-hidden visually-hidden--inline">${escapeHtml(settings.textSalePrice)}</span>
						</dt>
						<dt class="price__compare">
							<span class="visually-hidden visually-hidden--inline">${escapeHtml(settings.textRegularPrice)}</span>
						</dt>
						<dd class="price__last">
							<span class="price-item price-item--sale">${escapeHtml(moneyPrice)}</span>
						</dd>
						<dd class="price__compare">
							<span class="price-item price-item--regular">${escapeHtml(moneyCompareAt)}</span>
						</dd>
					</div>
				</dl>
			</div>
		`
	}

	function renderMedia(product, settings) {
		const image = product.featuredImage

		if (!image || !image.url) {
			return '<div class="external-collection__media-placeholder"></div>'
		}

		const fit = settings.mediaFit ? `object-fit: ${settings.mediaFit};` : ''
		const alt = escapeHtml(image.altText || product.title)

		return `
			<img
				src="${escapeHtml(imageUrlAtWidth(image.url, 720))}"
				srcset="${escapeHtml(buildSrcset(image))}"
				sizes="${escapeHtml(settings.sizes)}"
				alt="${alt}"
				loading="lazy"
				class="motion-reduce media--first"
				style="${escapeHtml(fit)}"
				width="${escapeHtml(image.width || '')}"
				height="${escapeHtml(image.height || '')}"
			>
		`
	}

	function renderBadge(product, settings) {
		const showExternal =
			settings.showExternalBadge && !!settings.externalBadgeText
		const soldOut = !product.availableForSale

		if (!showExternal && !soldOut) return ''

		// Mirrors the theme's own slots: custom badges left, stock badges right.
		const custom = showExternal
			? `
				<div class="card__badge-custom">
					<span class="badge badge-text badge--custom" aria-hidden="true">${escapeHtml(settings.externalBadgeText)}</span>
				</div>
			`
			: ''

		const standart = soldOut
			? `
				<div class="card__badge-standart">
					<span
						class="badge not-available-sold-out badge-text badge--bottom-left color-${escapeHtml(settings.badgeColor)}"
						aria-hidden="true"
					>${escapeHtml(settings.textSoldOut)}</span>
				</div>
			`
			: ''

		return `<div class="card__badge">${custom}${standart}</div>`
	}

	// Reuses the theme's quick-view slot (.quick-add + .card__link), so the
	// hover reveal, sizing and button styling all come from the theme's CSS.
	function renderExternalButton(href, settings) {
		if (!settings.showExternalButton || !settings.externalButtonText) return ''

		return `
			<div class="quick-add external-collection__cta">
				<a
					href="${href}"
					class="card__link button--${escapeHtml(settings.externalButtonStyle)}"
					tabindex="-1"
					aria-hidden="true"
				><span>${escapeHtml(settings.externalButtonText)}</span></a>
			</div>
		`
	}

	function renderCard(product, settings) {
		const href = escapeHtml(buildProductUrl(settings, product.handle))
		const title = escapeHtml(product.title)

		// Always announced, whichever visual markers are switched on.
		const opens = escapeHtml(settings.textOpensExternal)

		const vendor =
			settings.showVendor && product.vendor
				? `
					<span class="visually-hidden">${escapeHtml(settings.textVendor)}</span>
					<div class="caption-with-letter-spacing">${escapeHtml(product.vendor)}</div>
				`
				: ''

		const price = settings.showPrice ? renderPrice(product, settings) : ''

		return `
			<li class="collection-product-card">
				<div class="card-wrapper" data-product="${escapeHtml(product.handle)}">
					<span class="visually-hidden">${title}</span>

					<div class="card card--product" tabindex="-1">
						<div class="card__inner full-unstyled-link">
							<div
								class="media media--transparent media--${escapeHtml(settings.mediaRatio)} media--hover-effect"
								style="padding-bottom: ${escapeHtml(settings.mediaPadding)};"
							>
								${renderMedia(product, settings)}
							</div>
							${renderExternalButton(href, settings)}
							${renderBadge(product, settings)}
						</div>
						<a
							href="${href}"
							class="link link--overlay card-wrapper__link--overlay card-product__link"
							aria-label="${title} — ${opens}"
						></a>
					</div>

					<div class="card-information">
						<div class="card-information__wrapper">
							${vendor}
							<div class="card-title_info">
								<div class="card__title">
									<a
										style="text-transform: ${escapeHtml(settings.titleTransform)}"
										class="full-unstyled-link card__title-link--external"
										href="${href}"
									>${title}<span class="visually-hidden"> — ${opens}</span></a>
								</div>
								${price}
							</div>
						</div>
					</div>
				</div>
			</li>
		`
	}

	function readSettings(container) {
		const data = container.dataset

		return {
			domain: data.storefrontDomain,
			token: data.storefrontToken,
			apiVersion: data.apiVersion,
			collectionHandle: data.collectionHandle,
			externalBaseUrl: data.externalBaseUrl,
			productPathPattern: data.productPathPattern,
			utmSource: data.utmSource,
			pageSize: Number(data.pageSize) || 24,
			mediaRatio: data.mediaRatio,
			mediaPadding: data.mediaPadding,
			mediaFit: data.mediaFit,
			sizes: data.sizes,
			showVendor: data.showVendor === 'true',
			showPrice: data.showPrice === 'true',
			badgeColor: data.badgeColor,
			titleTransform: data.titleTransform,
			textSoldOut: data.textSoldOut,
			textVendor: data.textVendor,
			textRegularPrice: data.textRegularPrice,
			textSalePrice: data.textSalePrice,
			textEmpty: data.textEmpty,
			textError: data.textError,
			showExternalButton: data.showExternalButton === 'true',
			externalButtonText: data.externalButtonText,
			externalButtonStyle: data.externalButtonStyle || 'primary',
			showExternalBadge: data.showExternalBadge === 'true',
			externalBadgeText: data.externalBadgeText,
			textOpensExternal: data.textOpensExternal,
		}
	}

	async function fetchPage(settings, cursor) {
		const endpoint = `https://${settings.domain}/api/${settings.apiVersion}/graphql.json`

		// A blank collection handle shows every product published to the channel.
		// Note that "all" is an Online Store / Liquid convenience collection — it
		// does not exist in the Storefront API, so leave the handle empty instead.
		const useCollection = !!settings.collectionHandle

		const variables = {
			first: settings.pageSize,
			after: cursor || null,
		}
		if (useCollection) variables.handle = settings.collectionHandle

		const response = await fetch(endpoint, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				Accept: 'application/json',
				'X-Shopify-Storefront-Access-Token': settings.token,
			},
			body: JSON.stringify({
				query: useCollection ? COLLECTION_QUERY : PRODUCTS_QUERY,
				variables: variables,
			}),
		})

		if (!response.ok) {
			throw new Error(`Storefront API responded with ${response.status}`)
		}

		const payload = await response.json()

		if (payload.errors && payload.errors.length) {
			throw new Error(payload.errors[0].message || 'Storefront API error')
		}

		if (!payload.data) throw new Error('Storefront API returned no data')

		if (useCollection) {
			const collection = payload.data.collection
			if (!collection) {
				throw new Error(
					`Collection "${settings.collectionHandle}" was not found. Check the handle, and make sure the collection is published to the sales channel this token belongs to.`
				)
			}
			return collection.products
		}

		return payload.data.products
	}

	function showMessage(status, message) {
		if (!status) return
		status.classList.add('external-collection__status--message')
		status.innerHTML = `<p>${escapeHtml(message)}</p>`
		status.hidden = false
	}

	function initSection(root) {
		const containers = root.querySelectorAll('[data-external-collection]')

		containers.forEach((container) => {
			if (container.dataset.initialised === 'true') return
			container.dataset.initialised = 'true'

			const settings = readSettings(container)
			const list = container.querySelector('[data-external-collection-list]')
			const status = container.querySelector('[data-external-collection-status]')
			const loadMore = container.querySelector(
				'[data-external-collection-load-more]'
			)
			const loadMoreButton = container.querySelector(
				'[data-external-collection-load-more-button]'
			)

			let cursor = null
			let loading = false

			async function load() {
				if (loading) return
				loading = true
				container.classList.add('external-collection__grid--loading')

				if (loadMoreButton) {
					loadMoreButton.setAttribute('disabled', '')
					loadMoreButton.classList.add('loading')
				}

				try {
					const products = await fetchPage(settings, cursor)
					const nodes = products.nodes || []

					if (!nodes.length && !list.children.length) {
						showMessage(status, settings.textEmpty)
						if (loadMore) loadMore.hidden = true
						return
					}

					list.insertAdjacentHTML(
						'beforeend',
						nodes.map((product) => renderCard(product, settings)).join('')
					)

					if (status) status.hidden = true

					cursor = products.pageInfo.endCursor
					if (loadMore) loadMore.hidden = !products.pageInfo.hasNextPage
				} catch (error) {
					console.error('External collection:', error)

					if (list.children.length) {
						if (loadMore) loadMore.hidden = true
					} else {
						showMessage(status, settings.textError)
					}
				} finally {
					loading = false
					container.classList.remove('external-collection__grid--loading')

					if (loadMoreButton) {
						loadMoreButton.removeAttribute('disabled')
						loadMoreButton.classList.remove('loading')
					}
				}
			}

			if (loadMoreButton) {
				loadMoreButton.addEventListener('click', load)
			}

			load()
		})
	}

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', function () {
			initSection(document)
		})
	} else {
		initSection(document)
	}

	document.addEventListener('shopify:section:load', function (event) {
		initSection(event.target)
	})
})()
